import Phaser from 'phaser'

import {
  EMBEDDED_PAUSE_SVG,
  EMBEDDED_POINTER_RING_SVG,
  EMBEDDED_UI_CLICK_WAV,
  INTRO_MUSIC,
  LOADING_TIPS,
} from '../assets/embeddedAssets'
import { ACTIVE_CAMPAIGN_ID } from '../config/campaign'
import {
  getCampaignEventTokenTextureKey,
  getCampaignEventTokenUrls,
  getCampaignSfxAudioKey,
  getCampaignSfxUrls,
  getCampaignThemeUrl,
  getCampaignTileTextureKey,
  getCampaignTileUrls,
  getCampaignVoiceAudioKey,
  getCampaignVoiceUrls,
} from '../config/campaignAssets'

export class BootScene extends Phaser.Scene {
  private titleText?: Phaser.GameObjects.Text

  private progressText?: Phaser.GameObjects.Text

  private tipText?: Phaser.GameObjects.Text

  private progressBox?: Phaser.GameObjects.Rectangle

  private progressBar?: Phaser.GameObjects.Rectangle

  private progressBarWidth = 420

  private progressBarHeight = 16

  private progressValue = 0

  private loadingTip = LOADING_TIPS[0]

  constructor() {
    super('BootScene')
  }

  preload(): void {
    this.cameras.main.setBackgroundColor('#050505')
    this.loadingTip = Phaser.Utils.Array.GetRandom(LOADING_TIPS)

    this.createLoadingUi()
    this.registerLoaderEvents()
    this.queueEmbeddedAssets()
    this.handleResize()
  }

  create(): void {
    const fadeTargets: Phaser.GameObjects.GameObject[] = []

    if (this.progressBox) fadeTargets.push(this.progressBox)
    if (this.progressBar) fadeTargets.push(this.progressBar)
    if (this.titleText) fadeTargets.push(this.titleText)
    if (this.progressText) fadeTargets.push(this.progressText)
    if (this.tipText) fadeTargets.push(this.tipText)

    if (fadeTargets.length === 0) {
      this.scene.start('MenuScene')
      return
    }

    this.tweens.add({
      targets: fadeTargets,
      alpha: 0,
      ease: 'Sine.easeInOut',
      delay: 260,
      duration: 420,
      onComplete: () => {
        this.scene.start('MenuScene')
      },
    })
  }

  private queueEmbeddedAssets(): void {
    this.load.setPath('')
    this.load.image('ui.pause', EMBEDDED_PAUSE_SVG)
    this.load.image('ui.pointerRing', EMBEDDED_POINTER_RING_SVG)
    if (EMBEDDED_UI_CLICK_WAV) {
      this.load.audio('ui-click', EMBEDDED_UI_CLICK_WAV)
    }

    if (INTRO_MUSIC) {
      this.load.audio('intro-theme', INTRO_MUSIC)
    }

    const campaignTheme = getCampaignThemeUrl(ACTIVE_CAMPAIGN_ID)
    if (campaignTheme) {
      this.load.audio('campaign-theme', campaignTheme)
    }

    const tileUrls = getCampaignTileUrls(ACTIVE_CAMPAIGN_ID)
    Object.entries(tileUrls).forEach(([tileKey, url]) => {
      const textureKey = getCampaignTileTextureKey(ACTIVE_CAMPAIGN_ID, tileKey)
      this.load.image(textureKey, url)
    })

    const tokenUrls = getCampaignEventTokenUrls(ACTIVE_CAMPAIGN_ID)
    Object.entries(tokenUrls).forEach(([tokenKey, url]) => {
      const textureKey = getCampaignEventTokenTextureKey(ACTIVE_CAMPAIGN_ID, tokenKey)
      this.load.image(textureKey, url)
    })

    const voiceUrls = getCampaignVoiceUrls(ACTIVE_CAMPAIGN_ID)
    Object.entries(voiceUrls).forEach(([voiceKey, url]) => {
      const audioKey = getCampaignVoiceAudioKey(ACTIVE_CAMPAIGN_ID, voiceKey)
      this.load.audio(audioKey, url)
    })

    const sfxUrls = getCampaignSfxUrls(ACTIVE_CAMPAIGN_ID)
    Object.entries(sfxUrls).forEach(([sfxKey, url]) => {
      const audioKey = getCampaignSfxAudioKey(ACTIVE_CAMPAIGN_ID, sfxKey)
      this.load.audio(audioKey, url)
    })
  }

  private createLoadingUi(): void {
    this.titleText = this.add.text(0, 0, 'Preparando partida…', {
      fontFamily: 'Segoe UI, system-ui, sans-serif',
      color: '#f9fafb',
      fontStyle: 'bold',
      align: 'center',
    })

    this.progressBox = this.add.rectangle(0, 0, 10, 10, 0x111827, 0.65)
    this.progressBox.setOrigin(0.5, 0.5)

    this.progressBar = this.add.rectangle(0, 0, 10, 10, 0xf7b538, 0.95)
    this.progressBar.setOrigin(0, 0.5)

    this.progressText = this.add.text(0, 0, 'Cargando 0%', {
      fontFamily: 'Segoe UI, system-ui, sans-serif',
      color: '#e5e7eb',
      align: 'center',
    })

    this.tipText = this.add.text(0, 0, this.loadingTip, {
      fontFamily: 'Segoe UI, system-ui, sans-serif',
      color: '#9ca3af',
      align: 'center',
      wordWrap: { width: 600 },
    })

    this.scale.on('resize', this.handleResize, this)
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off('resize', this.handleResize, this)
      this.load.off(Phaser.Loader.Events.PROGRESS, this.handleProgress, this)
      this.load.off(Phaser.Loader.Events.COMPLETE, this.handleComplete, this)
    })
  }

  private registerLoaderEvents(): void {
    this.load.on(Phaser.Loader.Events.PROGRESS, this.handleProgress, this)
    this.load.once(Phaser.Loader.Events.COMPLETE, this.handleComplete, this)
  }

  private handleProgress(value: number): void {
    this.progressValue = Phaser.Math.Clamp(value, 0, 1)
    this.updateProgressUi()
  }

  private handleComplete(): void {
    this.progressValue = 1
    this.updateProgressUi()
    this.progressText?.setText('¡Todo listo!')
  }

  private handleResize = (): void => {
    const { width, height } = this.scale
    const centerX = width / 2
    const centerY = height / 2
    const minDimension = Math.min(width, height)

    this.progressBarWidth = Phaser.Math.Clamp(Math.round(width * 0.6), 260, 640)
    this.progressBarHeight = Phaser.Math.Clamp(Math.round(height * 0.018), 10, 26)

    const titleFont = Math.max(28, Math.round(minDimension * 0.06))
    const progressFont = Math.max(16, Math.round(minDimension * 0.035))
    const tipFont = Math.max(14, Math.round(minDimension * 0.028))

    const barY = centerY + Math.max(-40, -minDimension * 0.08)
    const progressTextY = barY + Math.max(36, this.progressBarHeight * 2.4)
    const tipY = progressTextY + Math.max(54, this.progressBarHeight * 3)

    this.titleText?.setFontSize(titleFont)
    this.titleText?.setPosition(centerX, centerY - Math.max(120, minDimension * 0.18))
    this.titleText?.setOrigin(0.5, 0.5)

    this.progressBox?.setSize(this.progressBarWidth + 28, this.progressBarHeight + 24)
    this.progressBox?.setPosition(centerX, barY)

    this.progressBar?.setPosition(centerX - this.progressBarWidth / 2, barY)

    this.progressText?.setFontSize(progressFont)
    this.progressText?.setPosition(centerX, progressTextY)
    this.progressText?.setOrigin(0.5, 0.5)

    this.tipText?.setFontSize(tipFont)
    this.tipText?.setPosition(centerX, tipY)
    this.tipText?.setOrigin(0.5, 0.5)
    this.tipText?.setWordWrapWidth(Math.min(width * 0.85, 720), true)

    this.updateProgressUi()
  }

  private updateProgressUi(): void {
    if (this.progressBar) {
      const effectiveWidth = this.progressBarWidth * this.progressValue
      this.progressBar.setSize(Math.max(effectiveWidth, 4), this.progressBarHeight)
    }

    if (this.progressText) {
      const percentage = Math.round(this.progressValue * 100)
      const label = this.progressText.text.startsWith('¡Todo listo!')
        ? '¡Todo listo!'
        : `Cargando ${percentage}%`
      this.progressText.setText(label)
    }

    if (this.progressBar) {
      this.progressBar.alpha = Phaser.Math.Linear(0.3, 1, this.progressValue)
    }
  }
}
